import { useState, useCallback, useEffect, useRef } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGifs } from '../hooks/useGifs';
import type { GifInfo, SendGifData } from '../types';

interface GifPickerProps {
  /** Триггер элемент для открытия пикера */
  children: React.ReactNode;
  /** Callback при выборе GIF */
  onGifSelect: (data: SendGifData) => void;
  /** Открыт ли пикер */
  open?: boolean;
  /** Callback при изменении состояния */
  onOpenChange?: (open: boolean) => void;
  /** Disabled состояние */
  disabled?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function GifPicker({
  children,
  onGifSelect,
  open,
  onOpenChange,
  disabled = false,
}: GifPickerProps) {
  const {
    savedGifs,
    searchResults,
    isLoading,
    isSearching,
    hasMore,
    fetchSavedGifs,
    searchGifs,
    clearSearch,
  } = useGifs();

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchMode, setIsSearchMode] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Загружаем сохранённые GIF при первом открытии
  useEffect(() => {
    if (open && savedGifs.length === 0 && !isSearchMode) {
      fetchSavedGifs();
    }
  }, [open, savedGifs.length, isSearchMode, fetchSavedGifs]);

  // Фокус на поиске при открытии
  useEffect(() => {
    if (open && isSearchMode) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [open, isSearchMode]);

  // Debounced поиск
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      clearSearch();
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchGifs(value.trim());
    }, 300);
  }, [searchGifs, clearSearch]);

  // Загрузка следующей страницы результатов
  const handleLoadMore = useCallback(() => {
    if (searchQuery.trim() && hasMore && !isSearching) {
      searchGifs(searchQuery.trim(), true);
    }
  }, [searchQuery, hasMore, isSearching, searchGifs]);

  // Обработка выбора GIF
  const handleSelectGif = useCallback((gif: GifInfo) => {
    onGifSelect({
      documentId: gif.documentId,
      accessHash: gif.accessHash,
      fileReference: gif.fileReference,
    });
    onOpenChange?.(false);
  }, [onGifSelect, onOpenChange]);

  // Переключение в режим поиска
  const handleEnterSearch = useCallback(() => {
    setIsSearchMode(true);
  }, []);

  // Выход из режима поиска
  const handleExitSearch = useCallback(() => {
    setIsSearchMode(false);
    setSearchQuery('');
    clearSearch();
  }, [clearSearch]);

  // При закрытии сбрасываем состояние
  useEffect(() => {
    if (!open) {
      setIsSearchMode(false);
      setSearchQuery('');
      clearSearch();
    }
  }, [open, clearSearch]);

  // Получить URL превью GIF
  const getGifPreviewUrl = useCallback((gif: GifInfo): string => {
    const params = new URLSearchParams({
      accessHash: gif.accessHash,
      fileReference: gif.fileReference,
    });
    return `${API_URL}/api/telegram-client/gifs/preview/${gif.documentId}?${params.toString()}`;
  }, []);

  const displayGifs = isSearchMode && searchQuery ? searchResults : savedGifs;
  const showLoading = isSearchMode ? isSearching : isLoading;
  const showEmpty = !showLoading && displayGifs.length === 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        className="w-[380px] p-0"
        side="top"
        align="end"
        sideOffset={8}
      >
        <div className="border-b p-2">
          {isSearchMode ? (
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={searchInputRef}
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Поиск GIF..."
                  className="pl-8 h-8"
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={handleExitSearch}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={handleEnterSearch}
            >
              <Search className="h-4 w-4 mr-2" />
              Поиск GIF...
            </Button>
          )}
        </div>

        <ScrollArea className="h-[320px]">
          {showLoading && displayGifs.length === 0 ? (
            <div className="flex items-center justify-center h-[200px]">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (showEmpty ? (
            <div className="flex items-center justify-center h-[200px] text-sm text-muted-foreground">
              {isSearchMode && searchQuery
                ? 'Ничего не найдено'
                : 'Нет сохранённых GIF'}
            </div>
          ) : (
            <div className="p-2">
              {/* Masonry-подобная сетка для GIF */}
              <div className="grid grid-cols-2 gap-1">
                {displayGifs.map((gif) => (
                  <GifItem
                    key={gif.id}
                    gif={gif}
                    onClick={() => handleSelectGif(gif)}
                    getPreviewUrl={getGifPreviewUrl}
                  />
                ))}
              </div>

              {/* Кнопка загрузки ещё */}
              {isSearchMode && hasMore && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-2"
                  onClick={handleLoadMore}
                  disabled={isSearching}
                >
                  {isSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  Загрузить ещё
                </Button>
              )}
            </div>
          ))}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

interface GifItemProps {
  gif: GifInfo;
  onClick: () => void;
  getPreviewUrl: (gif: GifInfo) => string;
}

function GifItem({ gif, onClick, getPreviewUrl }: GifItemProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Вычисляем соотношение сторон для правильного отображения
  const aspectRatio = gif.width && gif.height ? gif.width / gif.height : 1;
  const displayHeight = aspectRatio > 1.5 ? 80 : (aspectRatio < 0.67 ? 160 : 120);

  return (
    <button
      onClick={onClick}
      className={cn(
        'relative w-full rounded-lg overflow-hidden',
        'hover:ring-2 hover:ring-primary transition-all',
        'bg-muted'
      )}
      style={{ height: displayHeight }}
    >
      {!hasError ? (
        <>
          <img
            src={getPreviewUrl(gif)}
            alt={gif.title || 'GIF'}
            className={cn(
              'w-full h-full object-cover transition-opacity duration-200',
              isLoaded ? 'opacity-100' : 'opacity-0'
            )}
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
            loading="lazy"
          />
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          )}
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
          GIF
        </div>
      )}
    </button>
  );
}
